import type { TranscriptionMessage } from '@/store/slices/transcriptionSlice';

/**
 * Canonical transcript shape persisted by both live sync and final archive.
 *
 * Optional Redux fields are represented as explicit nulls here. This keeps
 * `false`, `0`, an empty string, and a removed value distinguishable when we
 * fingerprint a segment or send a metadata-only correction.
 */
export interface PersistedTranscriptItem {
  sync_id: string;
  speaker_name: string;
  message: string;
  timestamp_ms: number;
  is_final: boolean;
  participant_identity: string | null;
  is_self: boolean;
  is_officer: boolean | null;
  color: string;
  initial: string;
  segment_id: string | null;
  segment_seq: number | null;
  segment_key: string | null;
  bubble_id: string | null;
  audio_start_time: number | null;
  audio_end_time: number | null;
  /** Rule 57 rescue mark; null for a provider final. Mirrors the backend writer. */
  rescue: 'flush' | 'tail' | null;
}

/** Full camelCase payload used by archive producers without dropping metadata. */
export interface ArchiveTranscriptItem {
  syncId: string;
  name: string;
  message: string;
  timestamp: number;
  isFinal: boolean;
  isSelf: boolean;
  isOfficer: boolean | null;
  color: string;
  initial: string;
  participantIdentity: string | null;
  segmentId: string | null;
  segmentSeq: number | null;
  segmentKey: string | null;
  bubbleId: string | null;
  audioStartTime: number | null;
  audioEndTime: number | null;
  /** Rule 57 rescue mark; the archive is a third writer of the row and must carry it. */
  rescue: 'flush' | 'tail' | null;
}

export type PersistedTranscriptInput = Partial<PersistedTranscriptItem> & {
  speaker_name?: string | null;
  message?: string | null;
  timestamp_ms?: number | null;
};

type TranscriptIdentityInput = PersistedTranscriptInput | TranscriptionMessage;

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function finiteNumberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function identityPart(value: string): string {
  return encodeURIComponent(value);
}

/**
 * Return the immutable segment identity used by the delta protocol.
 *
 * Backend segment IDs are deterministic across tabs. Truly keyless legacy
 * rows use stable persisted fields plus their original ordinal; once this ID
 * is assigned it is written to `sync_id` and must be preserved on updates.
 */
export function transcriptSyncIdentity(
  item: TranscriptIdentityInput,
  index = 0,
): string {
  const record = item as unknown as Record<string, unknown>;
  const existing = nonEmptyString(record.sync_id) ?? nonEmptyString(record.syncId);
  if (existing) return existing;

  const participant =
    nonEmptyString(record.participant_identity) ??
    nonEmptyString(record.participantIdentity) ??
    nonEmptyString(record.speaker_name) ??
    nonEmptyString(record.name) ??
    'unknown';
  const segmentId = nonEmptyString(record.segment_id) ?? nonEmptyString(record.segmentId);
  if (segmentId) {
    return `segment-id:v1:${identityPart(participant)}:${identityPart(segmentId)}`;
  }
  const segmentKey = nonEmptyString(record.segment_key) ?? nonEmptyString(record.segmentKey);
  if (segmentKey) {
    return `segment-key:v1:${identityPart(participant)}:${identityPart(segmentKey)}`;
  }

  const audioStart =
    finiteNumberOrNull(record.audio_start_time) ??
    finiteNumberOrNull(record.audioStartTime);
  const timestamp =
    finiteNumberOrNull(record.timestamp_ms) ??
    finiteNumberOrNull(record.timestamp) ??
    0;
  const stableTime = audioStart === null ? `timestamp:${timestamp}` : `audio:${audioStart}`;
  const safeIndex = Number.isSafeInteger(index) && index >= 0 ? index : 0;

  return `legacy:v1:${identityPart(participant)}:${stableTime}:${safeIndex}`;
}

/** Convert a Redux segment to the one canonical persisted representation. */
export function toPersistedTranscript(
  message: TranscriptionMessage,
  index = 0,
): PersistedTranscriptItem {
  return {
    sync_id: transcriptSyncIdentity(message, index),
    speaker_name: message.name,
    message: message.message,
    timestamp_ms: message.timestamp,
    is_final: message.isFinal !== false,
    participant_identity: message.participantIdentity ?? null,
    is_self: message.isSelf,
    is_officer: message.isOfficer ?? null,
    color: message.color,
    initial: message.initial,
    segment_id: message.segmentId ?? null,
    segment_seq: finiteNumberOrNull(message.segmentSeq),
    segment_key: message.segmentKey ?? null,
    bubble_id: message.bubbleId ?? null,
    audio_start_time: finiteNumberOrNull(message.audioStartTime),
    audio_end_time: finiteNumberOrNull(message.audioEndTime),
    rescue: rescueMarkOrNull(message.rescue),
  };
}

/** Only the two marks the backend writes; anything else is not a rescue. */
export function rescueMarkOrNull(value: unknown): 'flush' | 'tail' | null {
  return value === 'flush' || value === 'tail' ? value : null;
}

/** Convert persisted/legacy DB content back to Redux without truthiness loss. */
export function fromPersistedTranscript(
  item: PersistedTranscriptInput,
  index = 0,
): TranscriptionMessage {
  const speakerName = item.speaker_name ?? '';
  return {
    syncId: transcriptSyncIdentity(item, index),
    name: speakerName,
    message: item.message ?? '',
    timestamp: item.timestamp_ms ?? 0,
    isFinal: item.is_final !== false,
    isSelf: item.is_self ?? false,
    isOfficer: item.is_officer ?? undefined,
    color: item.color ?? '#666',
    initial: item.initial ?? (speakerName.charAt(0).toUpperCase() || '?'),
    participantIdentity: item.participant_identity ?? undefined,
    segmentId: item.segment_id ?? undefined,
    segmentSeq: finiteNumberOrNull(item.segment_seq) ?? undefined,
    segmentKey: item.segment_key ?? undefined,
    bubbleId: item.bubble_id ?? undefined,
    audioStartTime: finiteNumberOrNull(item.audio_start_time) ?? undefined,
    audioEndTime: finiteNumberOrNull(item.audio_end_time) ?? undefined,
    rescue: rescueMarkOrNull(item.rescue) ?? undefined,
  };
}

/**
 * Collision-free client fingerprint of every persisted field.
 *
 * The object has a fixed property order and explicit nulls, so its canonical
 * JSON is safe to compare locally without retaining/serializing call history.
 */
export function transcriptFingerprint(item: PersistedTranscriptItem): string {
  return JSON.stringify(item);
}

/** Preserve every transcript field when crossing the archive API boundary. */
export function toArchiveTranscript(
  message: TranscriptionMessage,
  index = 0,
): ArchiveTranscriptItem {
  const persisted = toPersistedTranscript(message, index);
  return {
    syncId: persisted.sync_id,
    name: persisted.speaker_name,
    message: persisted.message,
    timestamp: persisted.timestamp_ms,
    isFinal: persisted.is_final,
    isSelf: persisted.is_self,
    isOfficer: persisted.is_officer,
    color: persisted.color,
    initial: persisted.initial,
    participantIdentity: persisted.participant_identity,
    segmentId: persisted.segment_id,
    segmentSeq: persisted.segment_seq,
    segmentKey: persisted.segment_key,
    bubbleId: persisted.bubble_id,
    audioStartTime: persisted.audio_start_time,
    audioEndTime: persisted.audio_end_time,
    rescue: persisted.rescue,
  };
}
